<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { t } from 'svelte-i18n';
  import { getUsersForDropdown, getActivosForDropdown, createActivoDropAssignment } from '../controllers/activoDropController.js';
  import { toasts } from 'svelte-toasts';
  import { GOOGLE_MAPS_API_KEY } from '../config.js';

  const dispatch = createEventDispatcher();

  export let isOpen = false;

  let users = [];
  let activos = [];
  let selectedUser = '';
  let selectedActivo = '';
  let returnDate = '';
  let selectedStatus = 'PENDING'; // Default status for new assignments
  let isLoading = false;
  let mapContainer;
  let map = null;
  let marker = null;
  let selectedLocation = null;
  let isMapLoaded = false;
  let isMapLoading = false;
  let mapError = null;

  // Simple autocomplete state
  let userQuery = '';
  let activoQuery = '';
  let showUserSuggestions = false;
  let showActivoSuggestions = false;
  let filteredUsers = [];
  let filteredActivos = [];

  // Utility to normalize strings (case/accents insensitive)
  function normalizeString(text) {
    return (text || '').toString().toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  $: filteredUsers = userQuery
    ? users.filter(u => {
        const q = normalizeString(userQuery);
        return normalizeString(u.name).includes(q) || normalizeString(u.email).includes(q);
      }).slice(0, 10)
    : [];

  $: filteredActivos = activoQuery
    ? activos.filter(a => {
        const q = normalizeString(activoQuery);
        return normalizeString(a.nombre).includes(q);
      }).slice(0, 10)
    : [];

  function onUserInput(event) {
    userQuery = event.target.value;
    selectedUser = '';
    showUserSuggestions = true;
  }

  function onActivoInput(event) {
    activoQuery = event.target.value;
    selectedActivo = '';
    showActivoSuggestions = true;
  }

  function selectUserSuggestion(user) {
    selectedUser = user.id;
    userQuery = `${user.name}${user.email ? ` (${user.email})` : ''}`;
    showUserSuggestions = false;
  }

  function selectActivoSuggestion(activo) {
    selectedActivo = activo.id;
    // Prefer nombre in input for simplicity
    activoQuery = `${activo.nombre || activo.name || ''}`.trim();
    showActivoSuggestions = false;
  }

  function hideUserSuggestions() {
    // Delay to allow click selection before blur hides
    setTimeout(() => { showUserSuggestions = false; }, 150);
  }

  function hideActivoSuggestions() {
    setTimeout(() => { showActivoSuggestions = false; }, 150);
  }

  onMount(async () => {
    await loadDropdownData();
  });

  // Watch for modal open state to load map
  $: if (isOpen && !isMapLoaded) {
    setTimeout(() => {
      if (!window.google || !window.google.maps) {
        loadGoogleMaps();
      } else {
        initializeMap();
      }
    }, 100);
  }

  // Watch for modal close to cleanup - but don't call closeModal() to avoid infinite loops
  $: if (!isOpen && isMapLoaded) {
    // Just reset the map state without calling closeModal()
    resetForm();
    
    // Clean up map
    if (marker) {
      try {
        marker.setMap(null);
        marker = null;
      } catch (error) {
        // Silent cleanup error
      }
    }
    
    if (map) {
      try {
        // Remove all listeners
        google.maps.event.clearInstanceListeners(map);
        map = null;
      } catch (error) {
        // Silent cleanup error
      }
    }
    
    isMapLoaded = false; // Reset map loaded state
  }

  async function loadDropdownData() {
    try {
      const [usersData, activosData] = await Promise.all([
        getUsersForDropdown(),
        getActivosForDropdown()
      ]);
      users = usersData || [];
      activos = activosData || [];
    } catch (error) {
      toasts.error('Error al cargar los datos');
    }
  }

  function loadGoogleMaps() {
    // Check if Google Maps is already loaded globally
    if (window.google && window.google.maps) {
      initializeMap();
      return;
    }

    // Check if we're already loading the API
    if (window.googleMapsLoading) {
      return;
    }

    // Check if script tag already exists
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => {
        setTimeout(initializeMap, 100);
      });
      return;
    }

    window.googleMapsLoading = true;
    isMapLoading = true;
    mapError = null;

    // Load Google Maps API with async loading
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      window.googleMapsLoading = false;
      isMapLoading = false;
      setTimeout(initializeMap, 100);
    };
    
    script.onerror = () => {
      window.googleMapsLoading = false;
      isMapLoading = false;
      mapError = 'Error al cargar el mapa de Google';
      toasts.error('Error al cargar el mapa de Google');
    };

    document.head.appendChild(script);
  }

  function initializeMap() {
    if (!mapContainer || !window.google || !window.google.maps) {
      return;
    }

    try {
      // Initialize map centered on Guatemala
      map = new google.maps.Map(mapContainer, {
        center: { lat: 14.6349, lng: -90.5069 },
        zoom: 10,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });

      // Add click listener to place marker
      map.addListener('click', (event) => {
        placeMarker(event.latLng);
      });

      isMapLoaded = true;
    } catch (error) {
      toasts.error('Error al inicializar el mapa');
    }
  }

  function placeMarker(latLng) {
    try {
      // Remove existing marker
      if (marker) {
        marker.setMap(null);
      }

      // Create new marker
      marker = new google.maps.Marker({
        position: latLng,
        map: map,
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
              <circle cx="16" cy="16" r="12" fill="#3b82f6" stroke="white" stroke-width="3"/>
              <text x="16" y="20" text-anchor="middle" fill="white" font-size="12" font-weight="bold">📍</text>
            </svg>
          `),
          scaledSize: new google.maps.Size(32, 32),
          anchor: new google.maps.Point(16, 16)
        }
      });

      selectedLocation = {
        lat: latLng.lat(),
        lng: latLng.lng()
      };

      toasts.success('Ubicación seleccionada');
    } catch (error) {
      toasts.error('Error al colocar el marcador');
    }
  }

  function openModal() {
    isOpen = true;
    resetForm();
    
    // Initialize map when modal opens
    // The watch effect handles this now
  }

  function closeModal() {
    // Dispatch event to parent to close modal instead of modifying prop directly
    dispatch('close-modal');
    resetForm();
    
    // Clean up map
    if (marker) {
      try {
        marker.setMap(null);
        marker = null;
      } catch (error) {
        // Silent cleanup error
      }
    }
    
    if (map) {
      try {
        // Remove all listeners
        google.maps.event.clearInstanceListeners(map);
        map = null;
      } catch (error) {
        // Silent cleanup error
      }
    }
    
    isMapLoaded = false; // Reset map loaded state
  }

  function resetForm() {
    selectedUser = '';
    selectedActivo = '';
    returnDate = '';
    selectedStatus = 'PENDING';
    selectedLocation = null;
    isLoading = false;
    userQuery = '';
    activoQuery = '';
  }

  async function handleSubmit() {
    if (!selectedUser || !selectedActivo || !selectedStatus) {
      toasts.error('Por favor selecciona un usuario, un activo y un estado');
      return;
    }

    if (!selectedLocation) {
      toasts.error('Por favor selecciona una ubicación en el mapa');
      return;
    }

    if (!isMapLoaded || isMapLoading) {
      toasts.error('El mapa no está listo. Por favor espera a que se cargue completamente.');
      return;
    }

    isLoading = true;

    try {
      const assignmentData = {
        user: selectedUser,
        activo: selectedActivo,
        location: selectedLocation,
        return: returnDate || null,
        sta: selectedStatus // Use 'sta' field as per collection schema
      };

      const result = await createActivoDropAssignment(assignmentData);
      
      if (result) {
        toasts.success('Asignación creada correctamente');
        
        // Dispatch the event to parent component
        dispatch('assignment-created', result);
        
        // Close the modal
        closeModal();
      } else {
        toasts.error('Error al crear la asignación - no se recibió respuesta del servidor');
      }
    } catch (error) {
      toasts.error(`Error al crear la asignación: ${error.message || 'Error desconocido'}`);
    } finally {
      isLoading = false;
    }
  }
</script>

{#if isOpen}
  <div class="modal-overlay" on:click={closeModal}>
    <div class="modal-content" on:click|stopPropagation>
      <div class="modal-header">
        <h3>{$t('common.newAssetAssignment')}</h3>
        <button class="close-btn" on:click={closeModal}>&times;</button>
      </div>

      <form on:submit|preventDefault={handleSubmit}>
        <div class="modal-body">
          <div class="form-group">
            <label for="user-input">{$t('common.user')} *</label>
            <div class="autocomplete">
              <input
                id="user-input"
                type="text"
                class="form-input"
                placeholder={$t('common.searchPlaceholder')}
                bind:value={userQuery}
                on:input={onUserInput}
                on:focus={() => showUserSuggestions = true}
                on:blur={hideUserSuggestions}
                autocomplete="off"
              />
              {#if showUserSuggestions && filteredUsers.length > 0}
                <ul class="autocomplete-list">
                  {#each filteredUsers as user}
                    <li class="autocomplete-item" on:mousedown|preventDefault={() => selectUserSuggestion(user)}>
                      <span class="primary">{user.name}</span>
                      {#if user.email}
                        <span class="secondary">{user.email}</span>
                      {/if}
                    </li>
                  {/each}
                </ul>
              {/if}
            </div>
          </div>

          <div class="form-group">
            <label for="activo-input">{$t('common.asset')} *</label>
            <div class="autocomplete">
              <input
                id="activo-input"
                type="text"
                class="form-input"
                placeholder={$t('common.searchPlaceholder')}
                bind:value={activoQuery}
                on:input={onActivoInput}
                on:focus={() => showActivoSuggestions = true}
                on:blur={hideActivoSuggestions}
                autocomplete="off"
              />
              {#if showActivoSuggestions && filteredActivos.length > 0}
                <ul class="autocomplete-list">
                  {#each filteredActivos as activo}
                    <li class="autocomplete-item" on:mousedown|preventDefault={() => selectActivoSuggestion(activo)}>
                      <span class="primary">{activo.nombre || activo.name}</span>
                      {#if activo.codigo}
                        <span class="secondary">{activo.codigo}</span>
                      {/if}
                    </li>
                  {/each}
                </ul>
              {/if}
            </div>
          </div>

          <div class="form-group">
            <label for="return-date">{$t('common.returnDate')}</label>
            <input 
              type="date" 
              id="return-date" 
              bind:value={returnDate}
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="status-select">{$t('common.status')} *</label>
            <select 
              id="status-select" 
              bind:value={selectedStatus}
              class="form-select"
              required
            >
              <option value="PENDING">{$t('common.pending')}</option>
              <option value="CONFIRMED">{$t('common.confirmed')}</option>
              <option value="RETURNED">{$t('common.returned')}</option>
            </select>
          </div>

          <div class="form-group">
            <label>{$t('common.locationOnMap')} *</label>
            <div class="map-container">
              {#if isMapLoading}
                <div class="map-loading">
                  <div class="spinner"></div>
                  <p>{$t('common.loadingMap')}</p>
                </div>
              {:else if mapError}
                <div class="map-error">
                  <p>⚠️ {mapError}</p>
                  <button type="button" class="retry-btn" on:click={loadGoogleMaps}>
                    Reintentar
                  </button>
                </div>
              {:else}
                <div bind:this={mapContainer} class="google-map"></div>
              {/if}
              
              <div class="map-instructions">
                <p>Haz clic en el mapa para seleccionar la ubicación</p>
                {#if selectedLocation}
                  <div class="location-info">
                    <span>📍 Ubicación: {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}</span>
                  </div>
                {/if}
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button 
            type="button" 
            class="btn btn-secondary" 
            on:click={closeModal}
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button 
            type="submit" 
            class="btn btn-primary" 
            disabled={isLoading || !selectedUser || !selectedActivo || !selectedStatus || !selectedLocation || !isMapLoaded || isMapLoading}
          >
            {#if isLoading}
              <span class="spinner"></span>
              {$t('common.creating')}
            {:else if !isMapLoaded || isMapLoading}
              <span class="spinner"></span>
              {$t('common.loadingMap')}
            {:else}
              {$t('common.createAssignment')}
            {/if}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}

<style>
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
  }

  .modal-content {
    background: white;
    border-radius: 12px;
    width: 100%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 24px 16px 24px;
    border-bottom: 1px solid #e2e8f0;
  }

  .modal-header h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #1e293b;
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #64748b;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    transition: all 0.2s;
  }

  .close-btn:hover {
    background-color: #f1f5f9;
    color: #475569;
  }

  .modal-body {
    padding: 24px;
  }

  .form-group {
    margin-bottom: 20px;
  }

  .form-group label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #374151;
  }

  .form-select,
  .form-input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
    transition: border-color 0.2s;
  }

  .form-select:focus,
  .form-input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  .map-container {
    margin-top: 10px;
  }

  .google-map {
    width: 100%;
    height: 300px;
    border-radius: 8px;
    border: 1px solid #d1d5db;
    margin-bottom: 10px;
  }

  .map-instructions {
    font-size: 14px;
    color: #6b7280;
  }

  .map-instructions p {
    margin: 0 0 8px 0;
  }

  .location-info {
    background: #eff6ff;
    padding: 8px 12px;
    border-radius: 6px;
    border: 1px solid #bfdbfe;
    color: #1e40af;
    font-weight: 500;
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 24px 24px 24px;
    border-top: 1px solid #e2e8f0;
  }

  .btn {
    padding: 10px 20px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-primary {
    background-color: #3b82f6;
    color: white;
  }

  .btn-primary:hover:not(:disabled) {
    background-color: #2563eb;
  }

  .btn-secondary {
    background-color: #6b7280;
    color: white;
  }

  .btn-secondary:hover:not(:disabled) {
    background-color: #4b5563;
  }

  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid transparent;
    border-top: 2px solid currentColor;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .map-loading,
  .map-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 300px; /* Match google-map height */
    background-color: #f9fafb;
    border: 1px dashed #e5e7eb;
    border-radius: 8px;
    padding: 20px;
    text-align: center;
  }

  .map-loading p,
  .map-error p {
    margin-top: 10px;
    color: #6b7280;
    font-size: 14px;
  }

  .retry-btn {
    margin-top: 10px;
    padding: 8px 15px;
    background-color: #3b82f6;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .retry-btn:hover:not(:disabled) {
    background-color: #2563eb;
  }

  /* Simple autocomplete styles */
  .autocomplete {
    position: relative;
  }

  .autocomplete-list {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-top: none;
    border-radius: 0 0 6px 6px;
    max-height: 220px;
    overflow-y: auto;
    z-index: 1100;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .autocomplete-item {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 12px;
    cursor: pointer;
  }

  .autocomplete-item:hover {
    background-color: #f3f4f6;
  }

  .autocomplete-item .primary {
    color: #111827;
  }

  .autocomplete-item .secondary {
    color: #6b7280;
    font-size: 12px;
  }
</style>
